import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

const apiClient = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// =====================================
// REQUEST INTERCEPTOR
// =====================================

apiClient.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();

        if (authStore.accessToken) {
            config.headers.Authorization =
                `Bearer ${authStore.accessToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// =====================================
// RESPONSE INTERCEPTOR
// =====================================

apiClient.interceptors.response.use(
    (response) => response,

    async (error) => {
        const authStore = useAuthStore();
        const originalRequest = error.config;

        const isRefreshRequest =
            originalRequest?.url?.includes('/auth/refresh/');

        if (
            error.response?.status === 401 &&
            !originalRequest?._retry &&
            !isRefreshRequest
        ) {
            originalRequest._retry = true;

            try {
                const response = await axios.post(
                    `${process.env.VUE_APP_API_BASE_URL}/auth/refresh/`,
                    {},
                    {
                        withCredentials: true,
                    }
                );

                const accessToken =
                    response.data.access ||
                    response.data.access_token ||
                    response.data.data?.access;

                authStore.setAccessToken(accessToken);

                originalRequest.headers.Authorization =
                    `Bearer ${accessToken}`;

                return apiClient(originalRequest);

            } catch (refreshError) {
                authStore.logout();

                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

class Request {

    constructor(url) {
        this.url = url;
    }

    // =====================================
    // POST
    // =====================================

    async post(raw, callback) {
        try {
            const response = await apiClient.post(
                this.url,
                raw
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // POST FORM DATA
    // =====================================

    async postFormData(raw, callback) {
        try {
            const response = await apiClient.post(
                this.url,
                raw,
                {
                    headers: {
                        'Content-Type':
                            'multipart/form-data',
                    },
                }
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // PATCH
    // =====================================

    async patch(raw, callback) {
        try {
            const response = await apiClient.patch(
                this.url,
                raw
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // PATCH FORM DATA
    // =====================================

    async patchFormData(raw, callback) {
        try {
            const response = await apiClient.patch(
                this.url,
                raw,
                {
                    headers: {
                        'Content-Type':
                            'multipart/form-data',
                    },
                }
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // PUT
    // =====================================

    async put(raw, callback) {
        try {
            const response = await apiClient.put(
                this.url,
                raw
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // GET
    // =====================================

    async get(callback) {
        try {
            const response = await apiClient.get(
                this.url
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // DELETE
    // =====================================

    async delete(callback, bodyData = {}) {
        try {
            const response = await apiClient.delete(
                this.url,
                {
                    data: bodyData,
                }
            );

            callback(response.data, undefined);

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                }
            );
        }
    }

    // =====================================
    // UPLOAD
    // =====================================

    async upload(raw, callback, params = null) {
        try {
            const response = await apiClient.post(
                this.url,
                raw,
                {
                    headers: {
                        'Content-Type':
                            'multipart/form-data',
                    },
                }
            );

            callback(
                response.data,
                undefined,
                params
            );

        } catch (err) {

            callback(
                undefined,
                err.response?.data || {
                    message: err.message,
                },
                params
            );
        }
    }
}

export default Request;